import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { useToast } from "../../context/ToastContext";
import api from "../../lib/api.ts";

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  // Form state
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  // UI state
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useToast();
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

  };

  const validateForm = () => {
    const newErrors = {};


    if (!formData.newPassword.trim()) {
      newErrors.newPassword = translate(
        "changePassword.validation.newPasswordRequired"
      );
    }
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = translate(
        "changePassword.validation.confirmPasswordRequired"
      );
    }

    // Password strength validation
    if (formData.newPassword && formData.newPassword.length < 8) {
      newErrors.newPassword = translate(
        "changePassword.validation.passwordLength"
      );
    }

    // Password match validation
    if (
      formData.newPassword &&
      formData.confirmPassword &&
      formData.newPassword !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = translate(
        "changePassword.validation.passwordsDoNotMatch"
      );
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError(translate("changePassword.messages.fixErrorsBeforeSubmitting"));
      return;
    }

    try {
      setIsLoading(true);

      // Get the token from localStorage
      const authUser = localStorage.getItem("authUser");
      let token = null;

      if (authUser) {
        try {
          const parsedAuthUser = JSON.parse(authUser);
          token = parsedAuthUser.token;
        } catch (error) {
          console.error("Error parsing authUser:", error);
        }
      }

      // Fallback to check other token locations
      if (!token) {
        token = localStorage.getItem("token");
      }

      if (!token) {
        throw new Error(
          translate("changePassword.messages.noAuthenticationToken")
        );
      }

      const passwordPayload = {
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
        password: formData.newPassword,
        passwordConfirmation: formData.confirmPassword,
        password_confirmation: formData.confirmPassword,
      };

      const response = await api.put(
        `/users/edit/${user.id}`,
        passwordPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success || /password/i.test(response.data?.message || '')) {
        showSuccess(translate("changePassword.messages.passwordChangedSuccessfully"));

        // Clear form
        setFormData({
          newPassword: "",
          confirmPassword: "",
        });

        // Logout user after successful password change (security best practice)
        setTimeout(() => {
          logout();
        }, 2000);
      }
    } catch (error) {
      console.error("Error changing password:", error);

      if (error.response?.status === 400) {
        showError(
          error.response?.data?.message ||
          translate("changePassword.messages.invalidPasswordFormat")
        );
      } else if (error.response?.status === 403) {
        showError(translate("changePassword.messages.noPermissionToChangePassword"));
      } else {
        showError(
          error.response?.data?.message ||
          translate("changePassword.messages.failedToChangePassword")
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({});
  };

  return (
    <div
      className="bg-card shadow-md rounded-[12px]"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px] '>
        <div className='px-4 sm:px-5 py-3 sm:py-4 text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold bg-primary-soft '>
          <h2 className='text-white text-sm sm:text-base md:text-lg font-semibold'>
            {translate("changePassword.title")}
          </h2>
        </div>
      </div>


      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start"
      >
        {/* Left side - inputs */}
        <div className="space-y-6 p-5 relative">
          {/* New Password */}
{/* New Password Field */}
<div className="flex-1">
  <label className="block text-white mb-2 text-[16px] text-left">
    {translate("changePassword.fields.newPassword")}{" "}
    <span className="text-red-500">*</span>
  </label>
  <div className="relative flex items-center">
    <input
      type={showNew ? "text" : "password"}
      name="newPassword"
      value={formData.newPassword}
      onChange={handleInputChange}
      placeholder={translate("changePassword.fields.newPasswordPlaceholder")}
      className={`w-full bg-page border rounded-[12px] p-3 pr-12 text-white placeholder:text-sm ${
        errors.newPassword ? "border-red-500" : "border-default"
      }`}
    />
    <button
      type="button"
      onClick={() => setShowNew(!showNew)}
      className="absolute right-4 text-gray-500 hover:text-gray-300 transition-colors"
    >
      {showNew ? (
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
        </svg>
      )}
    </button>
  </div>
  {errors.newPassword && (
    <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
  )}
</div>

{/* Confirm New Password Field */}
<div className="flex-1 mt-4">
  <label className="block text-white mb-2 text-[16px] text-left">
    {translate("changePassword.fields.confirmNewPassword")}{" "}
    <span className="text-red-500">*</span>
  </label>
  <div className="relative flex items-center">
    <input
      type={showConfirm ? "text" : "password"}
      name="confirmPassword"
      value={formData.confirmPassword}
      onChange={handleInputChange}
      placeholder={translate("changePassword.fields.confirmNewPasswordPlaceholder")}
      className={`w-full bg-page border rounded-[12px] p-3 pr-12 text-white placeholder:text-sm ${
        errors.confirmPassword ? "border-red-500" : "border-default"
      }`}
    />
    <button
      type="button"
      onClick={() => setShowConfirm(!showConfirm)}
      className="absolute right-4 text-gray-500 hover:text-gray-300 transition-colors"
    >
      {showConfirm ? (
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
          <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
        </svg>
      )}
    </button>
  </div>
  {errors.confirmPassword && (
    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
  )}
</div>


          {/* Password Requirements */}
          <div className="bg-page p-4 rounded-lg border border-default">
            <h4 className="text-sm font-semibold text-white mb-2">
              {translate("changePassword.requirements.title")}:
            </h4>
            <ul className="text-xs text-description space-y-1">
              <li>• {translate("changePassword.requirements.minLength")}</li>
              <li>• {translate("changePassword.requirements.mustMatch")}</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center sm:justify-end mr-0 sm:mr-[65px] mt-4 space-x-3">
            <button
              type='button'
              onClick={handleReset}
              disabled={isLoading}
              className='px-6 py-2 bg-page text-white rounded hover:bg-card transition disabled:opacity-50'
            >
              {translate("changePassword.buttons.reset")}
            </button>
            <button
              type='submit'
              disabled={isLoading}
              className='px-6 py-2 bg-primary text-white rounded hover:bg-primary-dark hover:text-white transition disabled:opacity-50 flex items-center gap-2'
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  {translate("changePassword.buttons.changing")}
                </>
              ) : (
                translate("changePassword.buttons.changePassword")
              )}
            </button>
          </div>
        </div>

        {/* Right side - Illustration placeholder */}
        <div className="hidden md:flex justify-center">
          <svg
            width="350"
            height="400"
            viewBox="0 0 578 674"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.12" clipPath="url(#clip0_60_12)">
              <path
                d="M513.516 512.933L513.505 513.237L527.075 674H428.053L415.218 575.264L409.568 671.468L307.88 670.202L318.681 543.896L327.867 485.35V485.338L329.246 476.527H510.816L511.053 479.717L513.516 512.933Z"
                fill="#2F2E41"
              />
              <path
                d="M251.829 606.128C262.316 608.268 273.252 595.037 276.254 576.573C277.568 568.491 277.144 560.746 275.373 554.413L276.981 543.77L303.966 400.59C303.966 400.59 353.658 289.227 353.656 269.893C353.655 250.56 339.282 241.079 339.282 241.079L319.832 241.283L258.838 408.181L247.854 531.513L246.077 548.433C242.425 553.66 239.59 560.739 238.277 568.821C235.274 587.285 241.342 603.987 251.829 606.128Z"
                fill="#1673C2"
              />
              <path
                d="M449.679 172.09L431.601 125.254L372.847 136.647L368.893 191.751L449.679 172.09Z"
                fill="#FFB6B6"
              />
              <path
                opacity="0.1"
                d="M449.679 172.09L431.601 125.254L372.847 136.647L368.893 191.751L449.679 172.09Z"
                fill="black"
              />
              <path
                d="M577.354 269.561C577.354 269.561 535.549 182.217 527.64 173.356C524.119 169.412 518.584 169.357 513.524 170.506L455.328 148.039L435.603 135.622L434.303 159.245L369.544 172.268C369.904 157.861 371.718 152.382 371.718 152.382L347.99 183.483L301.666 208.8L301.691 209.028C298.292 210.357 295.069 212.632 292.627 216.395C283.588 230.319 258.731 399.943 258.731 399.943L311.835 412.602L319.062 361.791L324.263 407.538L328.218 492.983C328.218 492.983 400.529 562.605 435.555 537.288C470.581 511.971 513.517 513.237 513.517 513.237L514.588 473.631C515.35 472.266 515.776 471.464 515.776 471.464L514.646 330.975V293.591C523.254 307.026 533.641 318.581 545.718 323.992C585.263 341.714 577.354 269.561 577.354 269.561Z"
                fill="#E6E6E6"
              />
              <path
                d="M396.083 144.887C425.935 144.887 450.134 117.775 450.134 84.3308C450.134 50.8865 425.935 23.7745 396.083 23.7745C366.231 23.7745 342.032 50.8865 342.032 84.3308C342.032 117.775 366.231 144.887 396.083 144.887Z"
                fill="#FFB6B6"
              />
              <path
                d="M434.859 101.445C434.859 101.445 439.559 83.0144 450.134 84.3308C460.71 85.6473 463.06 75.1157 460.71 69.85C458.36 64.5842 453.659 38.2553 453.659 38.2553C453.659 38.2553 456.01 19.8251 441.909 17.1923C427.809 14.5594 423.109 11.9265 420.759 6.66072C418.409 1.39494 383.158 -3.87083 371.407 4.02784C359.657 11.9265 350.257 27.0811 342.032 31.3518C333.806 35.6225 326.756 43.5211 331.456 56.6855C336.157 69.85 342.973 95.5968 342.973 95.5968C342.973 95.5968 354.957 90.913 357.307 98.8117C359.657 106.71 350.257 96.1788 362.007 75.1157C373.757 54.0526 373.757 32.9896 392.558 40.8882C411.358 48.7869 432.509 46.154 430.159 61.9513C427.809 77.7486 434.859 101.445 434.859 101.445Z"
                fill="#2F2E41"
              />
              <path
                d="M479 150C512.137 150 539 122.913 539 89.5002C539 56.087 512.137 29.0002 479 29.0002C445.863 29.0002 419 56.087 419 89.5002C419 122.913 445.863 150 479 150Z"
                fill="#135E9F"
              />
              <path
                d="M501.997 72.8969C491.68 82.4205 481.363 91.9441 471.046 101.468C469.814 104.85 463.275 98.9499 456.683 93.0648C454.407 91.0324 448.215 92.8604 450.497 94.898C458.138 101.72 465.7 108.565 467.997 106.115C469.304 107.281 472.891 107.309 474.184 106.115C485.517 95.6534 496.85 85.1917 508.183 74.7301C510.404 72.6801 504.219 70.8454 501.997 72.8969Z"
                fill="white"
              />
              <path
                d="M557.635 248.324L502.783 218.927C502.783 218.927 500.58 199.251 490.293 192.233C488.544 187.559 486.532 182.424 484.705 178.355C480.185 168.293 486.964 149.305 481.315 149.305C475.666 149.305 467.315 169.274 467.757 174.622C468.096 178.727 471.124 185.894 471.75 192.168C464.861 195.079 459.304 200.068 455.65 204.04C452.488 207.478 451.313 212.607 452.548 217.345C456.13 231.076 465.08 259.434 477.925 259.434C494.874 259.434 499.393 255.636 499.393 255.636C499.393 255.636 534.419 305.005 560.406 308.802C586.393 312.6 557.635 248.324 557.635 248.324Z"
                fill="#FFB6B6"
              />
            </g>
            <defs>
              <clipPath id="clip0_60_12">
                <rect width="578" height="674" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </form>
    </div>
  );
}
